"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/pl";
import { CalendarPlusIcon, LayersIcon, RotateCcwIcon } from "lucide-react";
import { cn } from "@/lib/utils";

dayjs.locale("pl");

// Atrybut na kafelku aktywności — klik poza nim zamyka tooltip (klik w inny kafelek go przenosi).
export const ACTIVITY_CELL_ATTR = "data-activity-cell";

type HoverState = { index: number; left: number; top: number; bottom: number };

// Wspólna obsługa tooltipa aktywności — używana przez heatmapę i baner statystyk,
// żeby zachowanie (hover na desktopie, tap na mobile, zamykanie) było identyczne.
export const useActivityTooltip = () => {
  const [hover, setHover] = useState<HoverState | null>(null);

  const show = (index: number) => (e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setHover({
      index,
      left: rect.left + rect.width / 2,
      top: rect.top,
      bottom: rect.bottom,
    });
  };

  const onCellEnter = show;
  const onCellLeave = () => setHover(null);
  // Mobile: brak hovera → tap na kafelek pokazuje tooltip (ten sam co na desktopie).
  const onCellClick = show;

  // Zamykanie: klik poza kafelkiem zamyka; klik w inny kafelek tylko przenosi popup.
  useEffect(() => {
    if (!hover) return;
    const close = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target?.closest(`[${ACTIVITY_CELL_ATTR}]`)) {
        setHover(null);
      }
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [hover]);

  return { hover, onCellEnter, onCellLeave, onCellClick };
};

export const ActivityTooltip = ({
  day,
  today,
  left,
  top,
  bottom,
}: {
  day: ExamLearningActivityDayVM;
  today: string;
  left: number;
  top: number;
  bottom: number;
}) => {
  const isFuture = day.date > today;
  const isToday = day.date === today;

  const plannedRows = [
    {
      icon: <RotateCcwIcon size={13} />,
      label: "Spaced repetition",
      value: day.plannedReviewCount,
    },
    {
      icon: <CalendarPlusIcon size={13} />,
      label: "Nowe pytania",
      value: day.plannedNewCount,
    },
  ];
  const doneRows = [
    {
      icon: <RotateCcwIcon size={13} />,
      label: "Spaced repetition",
      value: day.srCount,
    },
    {
      icon: <LayersIcon size={13} />,
      label: "Podejścia",
      value: day.attemptsCount,
    },
  ];

  // Sekcje: przyszłość = plan; przeszłość = zrobione; dziś = i plan (z zaległymi), i zrobione —
  // żeby user widział pełen plan na dziś obok tego, co już zrobił.
  const sections: { title: string | null; rows: typeof plannedRows }[] = isToday
    ? [
        { title: "Plan na dziś", rows: plannedRows },
        { title: "Zrobione dziś", rows: doneRows },
      ]
    : isFuture
      ? [{ title: null, rows: plannedRows }]
      : [{ title: null, rows: doneRows }];

  const isEmpty = sections.every((s) => s.rows.every((r) => r.value === 0));

  // Lewy akcent: zielony gdy była/jest aktywność, niebieski gdy tylko zaplanowana (przyszłość).
  const accent = isEmpty ? null : isFuture ? "bg-sky-500" : "bg-emerald-500";

  // Tooltip domyślnie nad kafelkiem, wyśrodkowany (-translate-x-1/2). Po zmierzeniu:
  // 1) przycinamy środek w poziomie do viewportu (skrajne kafelki),
  // 2) gdy nad kafelkiem brak miejsca (np. baner na górze strony) — pokazujemy pod nim.
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left, top: top - 8, flip: false });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const margin = 8;
    const half = el.offsetWidth / 2;
    const min = half + margin;
    const max = window.innerWidth - half - margin;
    const clampedLeft =
      max < min ? window.innerWidth / 2 : Math.min(Math.max(left, min), max);
    const flip = top - margin - el.offsetHeight < 0;
    setPos({
      left: clampedLeft,
      top: flip ? bottom + margin : top - margin,
      flip,
    });
  }, [left, top, bottom]);

  return (
    <div
      ref={ref}
      style={{ position: "fixed", left: pos.left, top: pos.top }}
      className={cn(
        "pointer-events-none z-50 -translate-x-1/2 overflow-hidden rounded-xl border border-zinc-200 bg-white px-4 py-3 text-zinc-900 shadow-lg",
        !pos.flip && "-translate-y-full",
      )}
    >
      {accent && (
        <span
          className={cn("absolute inset-y-2 left-0 w-1 rounded-full", accent)}
        />
      )}

      <div className="text-sm font-semibold">
        {dayjs(day.date).format("ddd D MMM YYYY")}
      </div>

      <div className="mt-2 space-y-2">
        {sections.map((section) => (
          <div key={section.title ?? "rows"} className="space-y-1.5">
            {section.title && (
              <div className="text-[11px] font-medium tracking-wide text-zinc-400 uppercase">
                {section.title}
              </div>
            )}
            {section.rows.map((row) => (
              <div
                key={row.label}
                className="flex items-center justify-between gap-8 text-xs"
              >
                <span className="flex items-center gap-1.5 text-zinc-500">
                  {row.icon}
                  {row.label}
                </span>
                <span className="font-semibold">{row.value}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {day.isExam ? (
        <div className="mt-2 text-xs font-medium text-pink-500">
          Dzień egzaminu
        </div>
      ) : (
        isEmpty && (
          <div className="mt-2 text-xs text-zinc-400">
            {isFuture ? "Brak zaplanowanej nauki" : "Brak aktywności"}
          </div>
        )
      )}
    </div>
  );
};
